import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Button,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  makeStyles
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { ArrowBack } from '@material-ui/icons'
import { Spacer } from '@vestaboard/installables'
import { FormattedDateTime } from '../formatted-date-time'
import { INSIGHTS_API_BASE_URL } from '../../constants'
import { AnalyticsMessageAppearanceCell } from './AnalyticsMessageAppearanceCell'
import {
  orderedAppearanceIdsFromRows,
  useBatchedMessageAppearancePreviews
} from './useBatchedMessageAppearancePreviews'

const PAGE_LIMIT = 50

const useStyles = makeStyles(theme => ({
  headCell: {
    fontWeight: 600,
    background: '#eee'
  },
  loadMoreWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  countCaption: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  previewHeadCell: {
    fontWeight: 600,
    background: '#eee',
    minWidth: 220
  },
  previewCell: {
    verticalAlign: 'top'
  }
}))

type MessageAppearanceActionRow = {
  eventTimestamp: string
  reportType: string
  subscriptionId: string
  messageAppearanceId: string | null
  errorMessage: string | null
}

type SuccessBody = {
  success: true
  actions: MessageAppearanceActionRow[]
  nextCursor: string | null
}

type ErrorBody = {
  success: false
  error: string
}

function insightsBaseUrl(): string | undefined {
  const raw = INSIGHTS_API_BASE_URL
  return raw?.replace(/\/$/, '') || undefined
}

export const MessageAppearanceActionAnalyticsPage: React.FC = () => {
  const { id: subscriptionId } = useParams<{ id: string }>()
  const { getAccessTokenSilently } = useAuth0()
  const classes = useStyles()
  const baseUrl = insightsBaseUrl()

  const [authLoading, setAuthLoading] = useState(true)
  const [token, setToken] = useState<string | null>(null)
  const [rows, setRows] = useState<MessageAppearanceActionRow[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const appendInFlightRef = useRef(false)
  const fetchGenerationRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const t = await getAccessTokenSilently()
        if (!cancelled) setToken(t)
      } catch {
        if (!cancelled) setToken(null)
      } finally {
        if (!cancelled) setAuthLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently])

  const fetchPage = useCallback(
    async (continueToken: string | null, append: boolean) => {
      if (!baseUrl || !token) return

      if (append) {
        if (appendInFlightRef.current) return
        appendInFlightRef.current = true
        setLoadingMore(true)
      } else {
        appendInFlightRef.current = false
        setLoadingMore(false)
        setInitialLoading(true)
      }
      setError(null)

      const requestId = ++fetchGenerationRef.current
      const isCurrent = () => requestId === fetchGenerationRef.current

      const url = new URL(
        `${baseUrl}/subscriptions/${encodeURIComponent(subscriptionId)}/message-appearance-action-analytics`
      )
      url.searchParams.set('limit', String(PAGE_LIMIT))
      if (continueToken) url.searchParams.set('continue', continueToken)

      let res: Response
      try {
        res = await fetch(url.toString(), {
          headers: { 'X-Vestaboard-Token': token }
        })
      } catch (e) {
        if (!isCurrent()) return
        setError(e instanceof Error ? e.message : 'Request failed')
        setInitialLoading(false)
        setLoadingMore(false)
        appendInFlightRef.current = false
        return
      }

      if (!isCurrent()) return

      const text = await res.text()
      if (!isCurrent()) return

      let json: unknown
      try {
        json = JSON.parse(text)
      } catch {
        if (!isCurrent()) return
        setError(res.ok ? 'Invalid JSON response' : text || `HTTP ${res.status}`)
        setInitialLoading(false)
        setLoadingMore(false)
        appendInFlightRef.current = false
        return
      }

      if (!isCurrent()) return

      if (!res.ok) {
        const errMsg =
          typeof json === 'object' && json !== null && 'error' in json && typeof (json as ErrorBody).error === 'string'
            ? (json as ErrorBody).error
            : `HTTP ${res.status}`
        if (!isCurrent()) return
        setError(errMsg)
        setInitialLoading(false)
        setLoadingMore(false)
        appendInFlightRef.current = false
        return
      }

      const body = json as SuccessBody | ErrorBody
      if (!body.success) {
        if (!isCurrent()) return
        const errMsg =
          typeof body === 'object' && body !== null && 'error' in body && typeof (body as ErrorBody).error === 'string'
            ? (body as ErrorBody).error
            : typeof body !== 'object' || body === null
              ? String(body)
              : 'Unknown error'
        setError(errMsg)
        setInitialLoading(false)
        setLoadingMore(false)
        appendInFlightRef.current = false
        return
      }

      if (!isCurrent()) return

      setRows(prev => (append ? [...prev, ...body.actions] : body.actions))
      setNextCursor(body.nextCursor)
      setInitialLoading(false)
      setLoadingMore(false)
      appendInFlightRef.current = false
    },
    [baseUrl, token, subscriptionId]
  )

  useEffect(() => {
    if (!token || !baseUrl || authLoading) return
    setRows([])
    setNextCursor(null)
    fetchPage(null, false)
  }, [token, baseUrl, subscriptionId, authLoading, fetchPage])

  const onLoadMore = useCallback(() => {
    if (nextCursor && !loadingMore && !initialLoading) {
      fetchPage(nextCursor, true)
    }
  }, [nextCursor, loadingMore, initialLoading, fetchPage])

  useEffect(() => {
    const el = loadMoreRef.current
    if (!el || !nextCursor) return

    const obs = new IntersectionObserver(
      entries => {
        const hit = entries.some(e => e.isIntersecting)
        if (hit) onLoadMore()
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [nextCursor, onLoadMore])

  const orderedAppearanceIds = useMemo(() => orderedAppearanceIdsFromRows(rows), [rows])
  const { cache: appearancePreviewCache, pendingCount: appearancePreviewsPending } =
    useBatchedMessageAppearancePreviews(subscriptionId, orderedAppearanceIds)

  const subscriptionDetailPath = `/subscription/${subscriptionId}`

  if (!baseUrl) {
    return (
      <>
        <Button component={RouterLink} to={subscriptionDetailPath} startIcon={<ArrowBack />} size='small'>
          Back to subscription
        </Button>
        <Spacer size='large' />
        <Alert severity='warning'>
          Set <code>INSIGHTS_SERVICE_ENDPOINT</code> in root env to the insights service origin (no trailing
          slash).
        </Alert>
      </>
    )
  }

  if (authLoading) {
    return (
      <>
        <Button component={RouterLink} to={subscriptionDetailPath} startIcon={<ArrowBack />} size='small'>
          Back to subscription
        </Button>
        <Spacer size='large' />
        <CircularProgress />
      </>
    )
  }

  if (!token) {
    return (
      <>
        <Button component={RouterLink} to={subscriptionDetailPath} startIcon={<ArrowBack />} size='small'>
          Back to subscription
        </Button>
        <Spacer size='large' />
        <Alert severity='error'>Could not load an auth token.</Alert>
      </>
    )
  }

  return (
    <>
      <Button component={RouterLink} to={subscriptionDetailPath} startIcon={<ArrowBack />} size='small'>
        Back to subscription
      </Button>
      <Spacer size='large' />
      <Typography variant='h4'>Message appearance action analytics</Typography>
      <Typography variant='body2' className={classes.countCaption}>
        Rolling 5-day window, newest first. Showing {rows.length} event{rows.length === 1 ? '' : 's'} loaded
        {nextCursor ? ' (more available)' : rows.length > 0 ? ' (end of results)' : ''}.
      </Typography>
      <Spacer size='large' />

      {error && (
        <>
          <Alert severity='error'>{error}</Alert>
          <Spacer size='medium' />
        </>
      )}

      {!initialLoading && orderedAppearanceIds.length > 0 && appearancePreviewsPending > 0 ? (
        <>
          <LinearProgress />
          <Spacer size='small' />
        </>
      ) : null}

      {initialLoading ? (
        <CircularProgress />
      ) : (
        <Card>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>Time (UTC)</TableCell>
                <TableCell className={classes.headCell}>Report type</TableCell>
                <TableCell className={classes.previewHeadCell}>Appearance</TableCell>
                <TableCell className={classes.headCell}>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant='body2' color='textSecondary'>
                      No rows in the last 5 days for this subscription.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, i) => (
                  <TableRow key={`${row.eventTimestamp}-${row.reportType}-${i}`}>
                    <TableCell>
                      <FormattedDateTime value={new Date(row.eventTimestamp).getTime()} />
                    </TableCell>
                    <TableCell>{row.reportType}</TableCell>
                    <TableCell className={classes.previewCell}>
                      <AnalyticsMessageAppearanceCell
                        messageAppearanceId={row.messageAppearanceId}
                        preview={row.messageAppearanceId ? appearancePreviewCache[row.messageAppearanceId] : undefined}
                      />
                    </TableCell>
                    <TableCell>{row.errorMessage ?? '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {nextCursor && (
        <div ref={loadMoreRef} className={classes.loadMoreWrap}>
          <Button variant='outlined' color='primary' disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? <CircularProgress size={22} /> : 'Load more'}
          </Button>
        </div>
      )}
    </>
  )
}
